import React from 'react';
import { construireVariables, FAMILLES, JETONS_BOUTON_OU_LIEN, type VariableAffichee } from '../../lib/variablesEmail';

/**
 * Zone de saisie d'un message (ou d'un objet) où les variables `{{…}}` apparaissent comme des
 * pastilles de couleur — « Client · Prénom », « Bouton · Payer en ligne » — au lieu de codes.
 *
 * Le texte enregistré ne change pas d'un caractère : c'est toujours la même chaîne avec ses `{{…}}`
 * (et `{{…:lien}}` pour un lien). Seul l'affichage change ; les marqueurs de mise en forme (`**gras**`…)
 * restent du texte ordinaire, comme dans l'ancienne zone de texte.
 *
 * Compatibilité : la barre de mise en forme et l'insertion de variable (insertNotificationToken,
 * wrapNotificationSelection) travaillent sur un « élément de saisie » : début et fin de sélection,
 * focus, setSelectionRange. `handleRef.current` reçoit un objet qui offre exactement cela, avec des
 * positions comptées dans le texte enregistré (une pastille compte autant de caractères que son code).
 * Les 22 écrans qui passent un `useRef<HTMLTextAreaElement>` n'ont donc rien à changer.
 *
 * Le DOM reste « canonique » : du texte (retours à la ligne en `\n`, `white-space: pre-wrap`), des
 * pastilles, et un `<br>` final qui donne sa hauteur à la dernière ligne. Dès qu'une frappe laisse
 * autre chose (le navigateur aime fabriquer des <div>), on reconstruit depuis le texte et on remet
 * le curseur à sa place.
 */

const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const RE_JETON = /\{\{([a-zA-Z_]+)(?::lien)?\}\}/g;

const nomCourt = (v: VariableAffichee) => {
    const f = FAMILLES[v.famille].nom.replace(/^(Le |La |L'|L’|Mon )/, '');
    return f.charAt(0).toUpperCase() + f.slice(1);
};

/** Le texte de la pastille et sa couleur, d'après le catalogue et la liste de l'écran. */
function aspectPastille(code: string, mode: string, info: VariableAffichee | undefined) {
    if (!info) return { libelle: code, couleur: 'light-dark(#5b6672, #8a94a1)', choix: false };
    const couleur = FAMILLES[info.famille].couleur;
    if (info.cliquable) {
        const m = mode === 'lien' ? 'Lien' : 'Bouton';
        return { libelle: `${m} · ${info.nom.replace(/[«»"]/g, '').trim()}`, couleur, choix: JETONS_BOUTON_OU_LIEN.has(code) };
    }
    if (code === 'logo') return { libelle: 'Logo', couleur, choix: false };
    return { libelle: info.famille === 'autres' ? info.nom : `${nomCourt(info)} · ${info.nom}`, couleur, choix: false };
}

const STYLE_PASTILLE = 'display:inline-flex;align-items:center;gap:5px;vertical-align:baseline;border-radius:999px;padding:0 9px 0 7px;margin:0 1px;'
    + 'font-size:.92em;font-weight:600;line-height:22px;white-space:nowrap;cursor:default;user-select:all;color:var(--g);'
    + 'background:color-mix(in srgb,var(--g) 11%,var(--bg-card));border:1px solid color-mix(in srgb,var(--g) 35%,var(--bg-card))';

function htmlPastille(code: string, mode: string, resolve: (c: string) => VariableAffichee | undefined): string {
    const a = aspectPastille(code, mode, resolve(code));
    const m = a.choix ? (mode === 'lien' ? 'lien' : 'bouton') : '';
    return `<span contenteditable="false" data-t="${code}" data-m="${m}" style="--g:${a.couleur};${STYLE_PASTILLE}"`
        + `${a.choix ? ' title="Cliquez pour choisir : bouton ou lien"' : ''}>`
        + `<i style="width:7px;height:7px;border-radius:50%;background:var(--g);display:inline-block"></i>${esc(a.libelle)}${a.choix ? '<span aria-hidden="true" style="opacity:.6">▾</span>' : ''}</span>`;
}

function versHtml(texte: string, resolve: (c: string) => VariableAffichee | undefined): string {
    let html = '';
    let dernier = 0;
    for (const m of texte.matchAll(RE_JETON)) {
        html += esc(texte.slice(dernier, m.index));
        html += htmlPastille(m[1], m[0].includes(':lien') ? 'lien' : 'bouton', resolve);
        dernier = (m.index ?? 0) + m[0].length;
    }
    html += esc(texte.slice(dernier));
    return `${html}<br data-fin="1">`;
}

/** Relit le texte enregistré depuis le DOM (toléré : <div>, <br> et espaces insécables du navigateur). */
function lire(parent: Node): string {
    let s = '';
    parent.childNodes.forEach((n, i) => {
        if (n.nodeType === 3) {
            s += (n.nodeValue || '').replace(/ /g, ' ').replace(/​/g, '');
            return;
        }
        const el = n as HTMLElement;
        if (el.dataset?.t) {
            s += `{{${el.dataset.t}${el.dataset.m === 'lien' ? ':lien' : ''}}}`;
        } else if (el.nodeName === 'BR') {
            if (i !== parent.childNodes.length - 1) s += '\n';
        } else if (/^(DIV|P)$/.test(el.nodeName)) {
            if (s && !s.endsWith('\n')) s += '\n';
            s += lire(el);
        } else {
            s += lire(el);
        }
    });
    return s;
}

function estCanonique(root: HTMLElement): boolean {
    const n = root.childNodes.length;
    if (n === 0 || root.lastChild?.nodeName !== 'BR') return false;
    for (let i = 0; i < n - 1; i++) {
        const c = root.childNodes[i] as HTMLElement;
        if (c.nodeType === 3) continue;
        if (!(c.dataset?.t)) return false;
    }
    return true;
}

/** Longueur du texte enregistré entre le début de la zone et un point du DOM. */
function decalage(root: HTMLElement, noeud: Node, position: number): number {
    const r = document.createRange();
    r.setStart(root, 0);
    r.setEnd(noeud, position);
    const tmp = document.createElement('div');
    tmp.appendChild(r.cloneContents());
    return lire(tmp).length;
}

/** Point du DOM (canonique) qui correspond à une position dans le texte enregistré. */
function pointDe(root: HTMLElement, cible: number): { noeud: Node; position: number } {
    let reste = Math.max(0, cible);
    for (let i = 0; i < root.childNodes.length - 1; i++) {
        const c = root.childNodes[i];
        if (c.nodeType === 3) {
            const l = (c.nodeValue || '').length;
            if (reste <= l) return { noeud: c, position: reste };
            reste -= l;
        } else {
            const el = c as HTMLElement;
            const t = `{{${el.dataset.t}${el.dataset.m === 'lien' ? ':lien' : ''}}}`.length;
            if (reste === 0) return { noeud: root, position: i };
            if (reste <= t) return { noeud: root, position: i + 1 };
            reste -= t;
        }
    }
    return { noeud: root, position: Math.max(0, root.childNodes.length - 1) };
}
interface PopupBouton { x: number; y: number; code: string; mode: string; el: HTMLElement }

export function EditeurMessage({
    handleRef, value, onChange, onFocus, tokens, uneLigne = false, placeholder, ariaLabel, rows = 10, choixBoutonLien = false,
}: {
    /** Reçoit l'objet « élément de saisie » (début/fin de sélection, focus, setSelectionRange). */
    handleRef: React.RefObject<any>;
    value: string;
    onChange: (v: string) => void;
    onFocus?: () => void;
    /** Les variables que l'e-mail sait remplir : donnent leur nom aux pastilles. */
    tokens: { token: string; label: string }[];
    uneLigne?: boolean;
    placeholder?: string;
    ariaLabel?: string;
    rows?: number;
    /** Un clic sur la pastille d'un lien/bouton propose « Bouton » ou « Lien » (e-mails de Notifications). */
    choixBoutonLien?: boolean;
}) {
    const ref = React.useRef<HTMLDivElement>(null);
    const derniere = React.useRef({ debut: 0, fin: 0 });
    const cleRendue = React.useRef('');
    const [popup, setPopup] = React.useState<PopupBouton | null>(null);

    // Clé stable : les écrans passent parfois un tableau neuf à chaque rendu, ce qui ne doit pas tout redessiner.
    const cle = tokens.map((t) => t.token + t.label).join('|');
    const resolve = React.useMemo(() => {
        const carte = new Map(construireVariables(tokens).map((v) => [v.code, v]));
        return (c: string) => carte.get(c);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cle]);

    const lireSelection = (): { debut: number; fin: number } | null => {
        const root = ref.current;
        const sel = window.getSelection();
        if (!root || !sel || !sel.rangeCount || !root.contains(sel.anchorNode)) return null;
        const r = sel.getRangeAt(0);
        const res = { debut: decalage(root, r.startContainer, r.startOffset), fin: decalage(root, r.endContainer, r.endOffset) };
        derniere.current = res;
        return res;
    };

    const poserSelection = (debut: number, fin: number = debut) => {
        const root = ref.current;
        const sel = window.getSelection();
        if (!root || !sel) return;
        const a = pointDe(root, debut);
        const b = pointDe(root, fin);
        const r = document.createRange();
        r.setStart(a.noeud, a.position);
        r.setEnd(b.noeud, b.position);
        sel.removeAllRanges();
        sel.addRange(r);
        derniere.current = { debut, fin };
        // Fait défiler la zone jusqu'au curseur : on voit où la variable vient d'arriver.
        const rect = r.getClientRects()[0] || (a.noeud.nodeType === 1 ? (a.noeud.childNodes[a.position - 1] as HTMLElement | undefined)?.getBoundingClientRect?.() : undefined);
        if (rect && rect.height) {
            const zone = root.getBoundingClientRect();
            if (rect.bottom > zone.bottom - 6) root.scrollTop += rect.bottom - zone.bottom + 10;
            else if (rect.top < zone.top + 6) root.scrollTop -= zone.top - rect.top + 10;
        }
    };

    // « Élément de saisie » exposé aux barres d'outils.
    React.useLayoutEffect(() => {
        const longueur = () => lire(ref.current as Node).length;
        handleRef.current = {
            get selectionStart() { return (lireSelection() ?? derniere.current).debut; },
            get selectionEnd() { return (lireSelection() ?? derniere.current).fin; },
            get value() { return lire(ref.current as Node); },
            focus: () => ref.current?.focus({ preventScroll: true }),
            blur: () => { /* la sélection reste : rien à faire */ },
            setSelectionRange: (a: number, b: number) => poserSelection(Math.min(a, longueur()), Math.min(b, longueur())),
        };
    });

    // Le DOM suit la valeur (et le nom des pastilles) ; une frappe de l'utilisateur ne le redessine pas.
    React.useLayoutEffect(() => {
        const root = ref.current;
        if (!root) return;
        if (lire(root) !== value || cleRendue.current !== cle || !estCanonique(root)) {
            const pos = document.activeElement === root ? lireSelection() : null;
            root.innerHTML = versHtml(value, resolve);
            cleRendue.current = cle;
            if (pos) poserSelection(pos.debut, pos.fin);
        }
    });

    /** Après toute modification du DOM : remettre en forme si besoin, puis prévenir. */
    const traiter = () => {
        const root = ref.current;
        if (!root) return;
        const sel = lireSelection();
        const v = lire(root);
        if (!estCanonique(root)) {
            root.innerHTML = versHtml(v, resolve);
            if (sel) poserSelection(sel.debut, sel.fin);
        }
        if (v !== value) onChange(v);
    };

    const insererTexte = (texte: string) => {
        const root = ref.current;
        const sel = window.getSelection();
        if (!root || !sel || !sel.rangeCount || !root.contains(sel.anchorNode)) return;
        const r = sel.getRangeAt(0);
        r.deleteContents();
        const noeud = document.createTextNode(texte);
        r.insertNode(noeud);
        r.setStartAfter(noeud);
        r.collapse(true);
        sel.removeAllRanges();
        sel.addRange(r);
        traiter();
    };

    const surClavier = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (!uneLigne) insererTexte('\n');
        }
    };
    const surCollage = (e: React.ClipboardEvent) => {
        e.preventDefault();
        let t = e.clipboardData.getData('text/plain').replace(/\r\n?/g, '\n');
        if (uneLigne) t = t.replace(/\n+/g, ' ');
        insererTexte(t);
    };
    const surClic = (e: React.MouseEvent) => {
        const chip = (e.target as HTMLElement).closest?.('[data-t]') as HTMLElement | null;
        if (!choixBoutonLien || !chip || !chip.dataset.m) return;
        const r = chip.getBoundingClientRect();
        setPopup({ x: r.left, y: r.bottom + 6, code: chip.dataset.t || '', mode: chip.dataset.m, el: chip });
    };
    const choisir = (mode: 'bouton' | 'lien') => {
        if (!popup) return;
        const tmp = document.createElement('div');
        tmp.innerHTML = htmlPastille(popup.code, mode, resolve);
        popup.el.replaceWith(tmp.firstChild as Node);
        setPopup(null);
        traiter();
    };

    React.useEffect(() => {
        if (!popup) return;
        const fermer = (e: Event) => {
            if (e instanceof KeyboardEvent && e.key !== 'Escape') return;
            if (e instanceof MouseEvent && (e.target as HTMLElement).closest?.('[data-popup-bouton]')) return;
            setPopup(null);
        };
        document.addEventListener('mousedown', fermer);
        document.addEventListener('keydown', fermer);
        return () => { document.removeEventListener('mousedown', fermer); document.removeEventListener('keydown', fermer); };
    }, [popup]);

    const info = popup ? resolve(popup.code) : undefined;

    return (
        <div className="relative">
            <div
                ref={ref}
                role="textbox"
                aria-multiline={!uneLigne}
                aria-label={ariaLabel}
                contentEditable
                suppressContentEditableWarning
                spellCheck
                onInput={traiter}
                onKeyDown={surClavier}
                onPaste={surCollage}
                onDrop={(e) => e.preventDefault()}
                onClick={surClic}
                onFocus={onFocus}
                onBlur={() => { lireSelection(); }}
                className={`w-full bg-surface-soft border border-border rounded-lg px-3 py-2 text-sm text-content outline-none focus:border-brand-500 ${
                    uneLigne ? 'whitespace-nowrap overflow-x-auto overflow-y-hidden leading-7' : 'whitespace-pre-wrap break-words overflow-y-auto leading-[1.9]'}`}
                style={uneLigne ? undefined : { minHeight: `${rows * 1.9 * 0.875 + 1.2}rem`, maxHeight: '60vh' }}
            />
            {value === '' && placeholder && (
                <span className="pointer-events-none absolute left-3 top-2 text-sm text-content-muted leading-7" aria-hidden="true">{placeholder}</span>
            )}
            {popup && info && (
                <div data-popup-bouton className="fixed z-[200] w-[290px] rounded-xl border border-border bg-surface p-3 shadow-xl"
                    style={{ left: Math.max(8, Math.min(popup.x, window.innerWidth - 298)), top: popup.y }}>
                    <p className="text-[12px] font-bold text-content mb-2">Afficher « {info.exemple} » en :</p>
                    <div className="grid grid-cols-2 gap-2">
                        {([['bouton', 'Bouton'], ['lien', 'Lien dans le texte']] as const).map(([m, nom]) => (
                            <button key={m} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => choisir(m)} aria-pressed={popup.mode === m}
                                className={`flex flex-col items-center gap-2 rounded-lg border px-2 py-2.5 text-[12px] font-semibold text-content ${popup.mode === m ? 'border-brand-500 bg-surface-soft' : 'border-border bg-surface-soft'}`}>
                                {m === 'bouton'
                                    ? <span className="rounded-md px-2 py-1 text-[11px] text-white bg-brand-600 max-w-full truncate">{info.exemple}</span>
                                    : <span className="underline text-[11.5px] text-brand-600 max-w-full truncate">{info.exemple}</span>}
                                {nom}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
